import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { AmaliLogo } from "./Logo";

const SECTIONS = ["about", "services", "repertoire", "gallery", "testimonials", "faq", "contact"];

export const Navbar = ({ lang, setLang, t }) => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-navy/70 border-b border-gold/20"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between h-20">
        <button
          onClick={() => go("hero")}
          data-testid="logo-home-button"
          className="group"
          aria-label="AMALI Home"
        >
          <AmaliLogo variant="compact" size={26} />
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {SECTIONS.map((s) => (
            <button
              key={s}
              onClick={() => go(s)}
              data-testid={`nav-${s}-link`}
              className="text-cream/80 hover:text-gold transition-colors duration-300 text-sm tracking-[0.18em] uppercase font-light"
            >
              {t.nav[s]}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="https://wa.me/385993509595"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-cta-book"
            className="hidden lg:inline-flex items-center gap-2 bg-gold text-navy px-5 py-2.5 text-[11px] uppercase tracking-[0.25em] font-semibold hover:bg-gold-soft transition-colors"
          >
            {t.floating?.book_now || "Rezerviraj"}
          </a>
          <LangToggle lang={lang} setLang={setLang} />
          <button
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden p-2 text-cream/90 hover:text-gold transition"
            data-testid="mobile-menu-button"
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden bg-navy/95 backdrop-blur-xl border-t border-gold/15"
            data-testid="mobile-menu-panel"
          >
            <div className="flex flex-col px-6 py-6 gap-5">
              {SECTIONS.map((s) => (
                <button
                  key={s}
                  onClick={() => go(s)}
                  data-testid={`mobile-nav-${s}-link`}
                  className="text-left text-cream/90 text-base uppercase tracking-[0.18em] font-light hover:text-gold"
                >
                  {t.nav[s]}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

const LangToggle = ({ lang, setLang }) => (
  <div
    className="flex items-center gap-0 border border-gold/30 rounded-none overflow-hidden"
    data-testid="language-toggle"
  >
    {["hr", "en"].map((code) => (
      <button
        key={code}
        onClick={() => setLang(code)}
        data-testid={`lang-${code}-button`}
        className={`px-3 py-1.5 text-xs tracking-[0.2em] uppercase font-medium transition-colors ${
          lang === code ? "bg-gold text-navy" : "text-cream/70 hover:text-gold"
        }`}
      >
        {code}
      </button>
    ))}
  </div>
);

export default Navbar;
